import React from "react";
import { IMG } from "@/lib/site";
import { useProduct } from "@/context/ProductContext";

// Render order + display labels for the spec keys that exist on the product doc.
// Any key NOT in this list is silently ignored; any listed key missing on the
// product is skipped (so the table self-heals as admin edits the CMS).
const SPEC_ROWS = [
  ["temperature_range", "Temperature range"],
  ["capacity", "Capacity"],
  ["power", "Power"],
  ["display", "Display"],
  ["lid", "Lid"],
  ["dimensions", "Dimensions"],
  ["weight", "Weight"],
  ["noise_level", "Noise level"],
  ["warranty", "Warranty"],
  ["delivery", "Delivery"],
];

export default function Specs() {
  const { product, loading } = useProduct();

  const rows = SPEC_ROWS
    .map(([key, label]) => [label, product?.specs?.[key]])
    .filter(([, value]) => Boolean(value));

  return (
    <section
      id="specs"
      data-testid="section-specs"
      className="bg-ice-light py-20 lg:py-28"
    >
      <div className="container-pc">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <p className="eyebrow">Engineered for precision</p>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-navy tracking-tighter">
              Specifications
            </h2>

            <dl className="mt-10 divide-y divide-silver/60 border-y border-silver/60" data-testid="specs-table">
              {loading && SPEC_ROWS.slice(0, 8).map(([key]) => (
                <div key={key} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 py-4">
                  <div className="sm:col-span-5 h-4 w-32 rounded bg-navy/10 animate-pulse" aria-hidden="true" />
                  <div className="sm:col-span-7 h-4 w-3/4 rounded bg-navy/10 animate-pulse" aria-hidden="true" />
                </div>
              ))}
              {!loading && rows.map(([label, value]) => (
                <div key={label} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 py-4">
                  <dt className="sm:col-span-5 text-sm text-muted">{label}</dt>
                  <dd className="sm:col-span-7 text-[15px] font-semibold text-navy">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <figure className="lg:col-span-5 rounded-2xl bg-white border border-silver/40 overflow-hidden shadow-card">
            <img
              src={IMG.altAngle}
              alt="PeptiChill cooler shown from an alternate three-quarter angle"
              className="block w-full max-w-full h-full object-contain blend-product"
              loading="lazy"
              width="800"
              height="800"
            />
          </figure>
        </div>
      </div>
    </section>
  );
}
